import React from 'react'
import styled, { css } from 'styled-components'
import { useRevealProps } from '../../MwPage/components/Reveal'
import { cyan, Eyebrow, Hi, Lede, onDark2, sans, Section, SectionHead, sky, Stack, ui } from '../../MwPage/styled'
import { useFocus } from '../focus'
import { useScores } from '../scores'
import { Column, Fig, FigNote, FigTitle, H2, Wrap } from '../styled'

const Ladder = styled.ol`
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
`

// the two tags are separate components, not one extending the other, so each state shows only its own
const tag = css`
  display: none;
  padding: 3px 10px;
  border-radius: 999px;
  font: 800 10.5px/1.3 ${ui};
  letter-spacing: 0.08em;
`

const TagHere = styled.span`
  ${tag}
  background: #ffffff;
  color: #17191c;
`

const TagNext = styled.span`
  ${tag}
  background: ${cyan};
  color: #0b1f24;
`

// a rung is past, here, next or ahead; the node and the line to the next rung colour with it
const Rung = styled.li`
  position: relative;
  padding: 0 0 22px 46px;

  &:last-child {
    padding-bottom: 6px;
  }

  i {
    position: absolute;
    left: 0;
    top: 2px;
    box-sizing: border-box;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #2c2f34;
    border: 2.5px solid #6b6f77;
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 9.5px;
    top: 30px;
    bottom: -2px;
    width: 3px;
    border-radius: 2px;
    background: repeating-linear-gradient(#4a4e56 0 3px, transparent 3px 8px);
  }

  > div > div {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  b {
    font: 800 17px/1.3 ${sans};
    letter-spacing: -0.01em;
    color: #b9bcc2;
  }

  p {
    margin: 2px 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${onDark2};
  }

  ${(props) =>
    props.$state === 'past' &&
    css`
      b {
        color: #8c9097;
      }

      i {
        background: ${sky};
        border-color: ${sky};
      }

      &::after {
        background: ${sky};
      }
    `}

  ${(props) =>
    props.$state === 'here' &&
    css`
      b {
        color: #ffffff;
      }

      i {
        background: #ffffff;
        border: 5px solid ${sky};
      }

      &::after {
        background: linear-gradient(${sky}, ${cyan});
      }

      ${TagHere} {
        display: inline-block;
      }
    `}

  ${(props) =>
    props.$state === 'next' &&
    css`
      b {
        color: #ffffff;
      }

      i {
        background: ${cyan};
        border-color: ${cyan};
        box-shadow: 0 0 0 6px rgba(31, 181, 204, 0.25);
      }

      ${TagNext} {
        display: inline-block;
      }
    `}
`

const Goal = styled.p`
  margin: 6px 0 0 46px;
  font: 800 12px/1.4 ${ui};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #2ec0d6;
`

const stateOf = (index, rung) =>
  index + 1 < rung ? 'past' : index + 1 === rung ? 'here' : index + 1 === rung + 1 ? 'next' : null

/** "Your stage": the ladder, with the rung the scorer placed the visitor on and the next one up. */
const StageSection = () => {
  const { stage } = useFocus()
  const { result } = useScores()

  return (
    <Section>
      <Wrap>
        <Stack $gap="clamp(24px, 3vw, 32px)">
          <SectionHead>
            <Eyebrow {...useRevealProps()}>{stage.eyebrow}</Eyebrow>
            <H2 {...useRevealProps()}>
              {stage.headline} <Hi>{stage.headlineAccent}</Hi>
            </H2>
            <Lede {...useRevealProps()}>
              {stage.ledeStart}
              {stage.ledeAfter}
            </Lede>
          </SectionHead>
          <Column $max={820} {...useRevealProps()}>
            <Fig>
              <FigTitle>{stage.figTitle}</FigTitle>
              <Ladder aria-live="polite">
                {stage.rungs.map((item, index) => (
                  <Rung key={item.title} $state={stateOf(index, result.rung)}>
                    <i aria-hidden="true" />
                    <div>
                      <div>
                        <b>{item.title}</b>
                        <TagHere>{stage.here}</TagHere>
                        <TagNext>{stage.next}</TagNext>
                      </div>
                      <p>{item.text}</p>
                    </div>
                  </Rung>
                ))}
              </Ladder>
              <Goal>
                {stage.goal}
                {stage.goalEnd}
              </Goal>
              <FigNote>{stage.figNote}</FigNote>
            </Fig>
          </Column>
        </Stack>
      </Wrap>
    </Section>
  )
}

export default StageSection
